import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollText, ArrowLeft } from "lucide-react";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-[#fbfdfc]">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Logo size={42} />
          <Link
            to="/apply"
            className="text-sm text-brand-700 hover:underline inline-flex items-center gap-1"
            data-testid="back-to-apply"
          >
            <ArrowLeft size={14} /> Back to application
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        <Card className="tj-card">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-brand-100 text-brand-700 grid place-items-center">
                <ScrollText size={20} />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-semibold text-ink-900">
                  Terms &amp; Conditions
                </h1>
                <p className="text-xs text-ink-600 mt-0.5">
                  Tulsi Jobs Placement Consultant
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-brand-100 bg-brand-50/60 p-4 mb-6">
              <p className="text-sm text-ink-800">
                By submitting your details on this platform and accepting any
                placement offered by Tulsi Jobs Placement Consultant, you
                acknowledge and agree to the following terms. Please read them
                carefully before pressing <strong>I AGREE</strong>.
              </p>
            </div>

            <Section title="1. Application & Use of Personal Data">
              <p>
                You voluntarily submit your personal and professional details
                (name, contact, qualifications, work history, CV, photograph,
                etc.) to Tulsi Jobs Placement Consultant (&quot;Tulsi Jobs&quot;,
                &quot;we&quot;, &quot;our&quot;) for the purpose of matching you
                with suitable employment opportunities. We may share your
                profile with prospective employers, partner companies, and our
                internal recruiters in furtherance of this objective.
              </p>
            </Section>

            <Section title="2. Employment Agreement (applies on placement)">
              <p>
                The following clauses constitute the standard employment
                agreement between Tulsi Jobs and any candidate placed via our
                consultancy. By accepting a job offered through Tulsi Jobs you
                agree to be bound by these clauses with the placing employer:
              </p>
              <SubClause title="2.1 Appointment">
                Tulsi Jobs has facilitated the placement of the Candidate with
                the placing company (&quot;Employer&quot;) for the role
                offered. The Candidate agrees to join the Employer in the
                offered position with effect from the joining date communicated
                in the offer letter.
              </SubClause>
              <SubClause title="2.2 Salary Structure">
                The monthly salary agreed upon for the position shall be as
                stated in the offer letter. During the first month
                (probationary month), the Candidate shall receive{" "}
                <strong>70% of the agreed monthly salary</strong>. The
                remaining <strong>30%</strong> of the first month&apos;s salary
                shall be deducted and retained as a{" "}
                <strong>consultancy / probation adjustment</strong> by Tulsi
                Jobs. From the second month of employment onwards, the
                Candidate shall receive <strong>100% of the agreed monthly
                salary</strong> directly from the Employer.
              </SubClause>
              <SubClause title="2.3 Probation Period">
                The first month of employment shall be treated as a probation
                period. Continuation of employment beyond the probation period
                is subject to (a) satisfactory performance as judged by the
                Employer and (b) adherence to the Employer&apos;s company
                policies. Tulsi Jobs is not responsible for the continuation
                decision; that decision rests solely with the Employer.
              </SubClause>
              <SubClause title="2.4 Acceptance">
                Pressing <strong>I AGREE</strong> on any digital communication
                (form submission, WhatsApp reply, email reply, in-app
                acknowledgement) is treated as a binding electronic acceptance
                of these clauses by the Candidate.
              </SubClause>
            </Section>

            <Section title="3. Honest Information">
              <p>
                You confirm that the information submitted is true, accurate
                and not misleading. Misrepresentation (forged certificates,
                fake experience, incorrect identity) may lead to rejection of
                application, blacklisting from our database, or — if discovered
                post-placement — termination by the Employer without any
                obligation on Tulsi Jobs to refund or compensate.
              </p>
            </Section>

            <Section title="4. No Guarantee of Employment">
              <p>
                Tulsi Jobs is a placement consultant, not the employer. We
                shortlist, recommend and facilitate interviews, but final
                hiring decisions rest solely with the Employer. Submission of
                your details, payment of ₹200 for resume download, attending
                interviews, or any other interaction with Tulsi Jobs does not
                guarantee employment, salary, location, or interview calls.
              </p>
            </Section>

            <Section title="5. Conduct & Communication">
              <p>
                We may contact you via phone calls, SMS, WhatsApp, or email
                regarding job opportunities and your application. You consent
                to receive such communications. You agree to respond honestly
                and respectfully to recruiters from Tulsi Jobs and from any
                placing Employer.
              </p>
            </Section>

            <Section title="6. Payments & Refunds">
              <p>
                The ₹200 resume-download fee is the only candidate-facing fee
                on this platform. All payments are governed by our{" "}
                <Link
                  to="/refund-policy"
                  className="text-brand-700 hover:underline"
                  data-testid="link-refund-policy"
                >
                  Refund &amp; Cancellation Policy
                </Link>
                . The consultancy/probation deduction described in clause 2.2
                is separate, post-placement, and is settled between the
                Candidate, the Employer and Tulsi Jobs offline (typically via
                first-month payroll).
              </p>
            </Section>

            <Section title="7. Limitation of Liability">
              <p>
                Tulsi Jobs Placement Consultant, its directors, employees, and
                affiliates shall not be liable for any direct, indirect,
                incidental, consequential or punitive damages arising out of or
                in connection with your use of this platform or any placement
                facilitated through it, including but not limited to delayed
                joining, work disputes, salary disputes with the Employer, or
                personal loss.
              </p>
            </Section>

            <Section title="8. Modifications">
              <p>
                We may update these terms from time to time. The latest version
                is always available on this page at <code>/terms</code>.
                Continued use of the platform after an update constitutes
                acceptance of the revised terms.
              </p>
            </Section>

            <Section title="9. Jurisdiction">
              <p>
                These terms shall be governed by and construed in accordance
                with the laws of India. Any disputes arising shall be subject
                to the exclusive jurisdiction of the courts in the city where
                Tulsi Jobs is headquartered.
              </p>
            </Section>

            <Section title="10. Contact">
              <p>
                For any question regarding these terms, write to{" "}
                <a
                  href="mailto:support@tulsijobs.com"
                  className="text-brand-700 hover:underline"
                >
                  support@tulsijobs.com
                </a>
                .
              </p>
            </Section>

            <div className="mt-8 rounded-xl border border-brand-200 bg-brand-50 p-5 text-center">
              <p className="text-sm text-ink-900">
                <strong>By clicking I AGREE</strong> on the application form,
                you confirm that you have read and accepted all the above
                Terms &amp; Conditions, including the salary &amp; probation
                clauses (2.2 &amp; 2.3) applicable on placement.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
      <footer className="text-center text-xs text-ink-600 py-8">
        © {new Date().getFullYear()} Tulsi Jobs Placement Consultant
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-6">
      <h2 className="font-heading text-base font-semibold text-ink-900 mb-2">
        {title}
      </h2>
      <div className="text-sm text-ink-700 leading-relaxed space-y-2">
        {children}
      </div>
    </section>
  );
}

function SubClause({ title, children }) {
  return (
    <div className="mt-3 pl-4 border-l-2 border-brand-200">
      <h3 className="font-medium text-ink-900 text-sm mb-1">{title}</h3>
      <p className="text-sm text-ink-700 leading-relaxed">{children}</p>
    </div>
  );
}
